// @spec GHG-FIX-020
'use client';

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertTriangle, Info, Plus, RefreshCcw, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useActivityData,
  useDeleteActivityData,
  useScope3ActivityDataSummary,
} from '@/features/ghg/api/use-activity-data';
import { useActivityGroups } from '@/features/ghg/api/use-activity-groups';
import { useFacilities } from '@/features/ghg/api/use-facilities';
import { useGhgOcrStatus } from '@/features/ghg/api/use-ghg-ocr-status';
import { ScopeCalculationsEntriesCard } from '@/features/ghg/components/ScopeCalculationsEntriesCard';
import { GhgDocumentUpload } from '@/features/ghg/components/ocr/GhgDocumentUpload';
import { LeasedAssetReviewDialog } from '@/features/ghg/components/ocr/LeasedAssetReviewDialog';
import {
  SCOPE3_CATEGORY_BY_NUMBER,
  SCOPE3_CATEGORY_DETAIL_BY_NUMBER,
  SCOPE3_CATEGORY_ROUTES,
  getScope3CategoryNeighbors,
} from '@/features/ghg/config/scope3-categories';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import {
  getScope3CategoryName,
  getScope3CategoryTitle,
} from '@/features/ghg/lib/category-labels';
import type {
  ActivityDataListFilters,
  ActivityDataSortBy,
  SortOrder,
} from '@/features/ghg/types/activity-data';
import { useTranslation } from 'react-i18next';

export const scope3SearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional(),
  facilityId: z.string().optional(),
  activityGroupId: z.string().optional(),
  dataQuality: z.enum(['measured', 'calculated', 'estimated']).optional(),
  status: z.enum(['draft', 'submitted', 'pending_approval', 'approved', 'rejected']).optional(),
  sortBy: z
    .enum([
      'createdAt',
      'estimatedCo2eKg',
      'quantity',
      'activityCategory',
      'activityType',
      'facilityName',
      'dataQualityScore',
      'status',
    ])
    .optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  scope3Category: z.coerce.number().int().min(1).max(15).optional(),
  missingEmissions: z
    .preprocess((value) => {
      if (value === true || value === 'true') return true;
      if (value === false || value === 'false') return false;
      return undefined;
    }, z.boolean().optional())
    .optional(),
});

type Scope3Search = z.infer<typeof scope3SearchSchema>;

export interface GhgScope3CalculationsPageProps {
  fixedScope3Category?: number;
}

const PAGE_SIZE = 50;

function CategoryGuidanceList({ title, items }: { title: string; items: string[] }) {
  return (
    <section className="rounded-lg border p-3">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

export function GhgScope3CalculationsPage({
  fixedScope3Category,
}: GhgScope3CalculationsPageProps = {}) {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const rawSearch = useSearch({ strict: false });
  const search = useMemo<Scope3Search>(() => {
    const parsed = scope3SearchSchema.safeParse(rawSearch);
    return parsed.success ? parsed.data : {};
  }, [rawSearch]);
  const { facilities } = useFacilities();
  const { groups } = useActivityGroups();
  const { periodId, period, periodRange } = useGHGPagePeriod();

  const effectiveScope3Category = fixedScope3Category ?? search.scope3Category;
  const selectedCategoryMeta =
    effectiveScope3Category != null
      ? SCOPE3_CATEGORY_BY_NUMBER.get(effectiveScope3Category)
      : undefined;
  const selectedCategoryDetail =
    effectiveScope3Category != null
      ? SCOPE3_CATEGORY_DETAIL_BY_NUMBER.get(effectiveScope3Category)
      : undefined;
  const categoryNeighbors =
    effectiveScope3Category != null
      ? getScope3CategoryNeighbors(effectiveScope3Category)
      : undefined;

  const currentSortBy: ActivityDataSortBy = search.sortBy ?? 'createdAt';
  const currentSortOrder: SortOrder = search.sortOrder ?? 'desc';
  const currentPage = search.page ?? 1;

  const filters = useMemo<ActivityDataListFilters>(
    () => ({
      reportingPeriodId: periodRange ? undefined : periodId,
      dateFrom: periodRange?.dateFrom,
      dateTo: periodRange?.dateTo,
      scope: 3,
      scope3Category: effectiveScope3Category,
      facilityId: search.facilityId,
      activityGroupId: search.activityGroupId,
      dataQuality: search.dataQuality,
      status: search.status,
      sortBy: currentSortBy,
      sortOrder: currentSortOrder,
      missingEmissions: search.missingEmissions,
      page: currentPage,
      pageSize: PAGE_SIZE,
    }),
    [
      currentPage,
      currentSortBy,
      currentSortOrder,
      effectiveScope3Category,
      search.activityGroupId,
      search.dataQuality,
      search.facilityId,
      search.missingEmissions,
      search.status,
      periodId,
      periodRange,
    ]
  );

  const summaryFilters = useMemo<ActivityDataListFilters>(
    () => ({
      reportingPeriodId: periodRange ? undefined : periodId,
      dateFrom: periodRange?.dateFrom,
      dateTo: periodRange?.dateTo,
      scope: 3,
      scope3Category: effectiveScope3Category,
      facilityId: search.facilityId,
      activityGroupId: search.activityGroupId,
      dataQuality: search.dataQuality,
      status: search.status,
      sortBy: currentSortBy,
      sortOrder: currentSortOrder,
      missingEmissions: search.missingEmissions,
    }),
    [
      currentSortBy,
      currentSortOrder,
      effectiveScope3Category,
      search.activityGroupId,
      search.dataQuality,
      search.facilityId,
      search.missingEmissions,
      search.status,
      periodId,
      periodRange,
    ]
  );

  const { entries, total, page, pageSize, isLoading, isError, error, refetch } =
    useActivityData(filters);
  const {
    summary,
    isLoading: summaryLoading,
    isError: summaryError,
    refetch: refetchSummary,
  } = useScope3ActivityDataSummary(summaryFilters);
  const deleteEntryMutation = useDeleteActivityData();

  // Leased asset upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [activeUploadId, setActiveUploadId] = useState<string | undefined>(undefined);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const ocrStatus = useGhgOcrStatus(activeUploadId);

  // When OCR completes, open the review dialog
  useEffect(() => {
    if (ocrStatus.status === 'completed' && activeUploadId && !showReviewDialog) {
      setShowUploadDialog(false);
      setShowReviewDialog(true);
    }
  }, [ocrStatus.status, activeUploadId, showReviewDialog]);

  const totalPages = Math.ceil(total / Math.max(pageSize, 1));

  const updateSearch = (patch: Partial<Scope3Search>) => {
    void navigate({
      to: '.',
      search: (prev) => {
        const next = { ...prev, ...patch } as Scope3Search;
        return Object.fromEntries(
          Object.entries(next).filter(([, value]) => value != null && value !== '')
        ) as Scope3Search;
      },
      replace: true,
    });
  };

  const onFilterChange = (patch: Record<string, unknown>) => {
    updateSearch({ page: 1, ...patch });
  };

  const onPageChange = (pageNumber: number) => {
    updateSearch({ page: pageNumber });
  };

  const scopeLabel = selectedCategoryMeta
    ? getScope3CategoryTitle(selectedCategoryMeta.number, t)
    : t('ghgScope3.scope3Emissions');
  const visibleEntryStats = useMemo(() => {
    let missingEstimates = 0;
    let approvedEntries = 0;
    let visibleEstimatedCo2eKg = 0;

    entries.forEach((entry) => {
      if (entry.estimatedCo2eKg == null) {
        missingEstimates += 1;
      } else {
        visibleEstimatedCo2eKg += entry.estimatedCo2eKg;
      }
      if (entry.status === 'approved') {
        approvedEntries += 1;
      }
    });

    return {
      visibleEntries: entries.length,
      missingEstimates,
      approvedEntries,
      visibleEstimatedCo2eKg,
    };
  }, [entries]);

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <header className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">
              {selectedCategoryMeta
                ? getScope3CategoryTitle(selectedCategoryMeta.number, t)
                : t('ghgScope3.scope3Emissions')}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {selectedCategoryMeta
                ? t('ghgScope3.categoryDescription', { number: selectedCategoryMeta.number, period: period?.name ?? t('ghgScope3.selectedReportingPeriod') })
                : t('ghgScope3.scope3Description', { period: period?.name ?? t('ghgScope3.selectedReportingPeriod') })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="secondary" size="sm">
              <Link to="/ghg/scope3/sold-products">{t('ghgScope3.soldProductGHG')}</Link>
            </Button>
            {selectedCategoryMeta && (
              <Button asChild variant="outline" size="sm">
                <Link to="/ghg/calculations/scope3">{t('ghgScope3.allScope3Categories')}</Link>
              </Button>
            )}
            {selectedCategoryMeta && categoryNeighbors?.previous && (
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/ghg/scope3/$category"
                  params={{ category: categoryNeighbors.previous.slug }}
                >
                  {t('ghgScope3.prevCat', { number: categoryNeighbors.previous.number })}
                </Link>
              </Button>
            )}
            {selectedCategoryMeta && categoryNeighbors?.next && (
              <Button asChild variant="outline" size="sm">
                <Link to="/ghg/scope3/$category" params={{ category: categoryNeighbors.next.slug }}>
                  {t('ghgScope3.nextCat', { number: categoryNeighbors.next.number })}
                </Link>
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void refetch();
              }}
              className="gap-2"
            >
              <RefreshCcw className="h-4 w-4" />
              {t('ghgScope3.refresh')}
            </Button>
            {effectiveScope3Category === 3 && (
              <Button asChild variant="secondary" size="sm">
                <Link
                  to="/ghg/scope3/$category/derive"
                  params={{ category: selectedCategoryMeta?.slug ?? 'fuel-and-energy-activities' }}
                >
                  {t('ghgScope3.generateFromScope12')}
                </Link>
              </Button>
            )}
            {(effectiveScope3Category === 8 || effectiveScope3Category === 13) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowUploadDialog(true);
                }}
                className="gap-2"
                data-testid="scope3-upload-data-btn"
              >
                <Upload className="h-4 w-4" />
                {t('ghgScope3.uploadData')}
              </Button>
            )}
            <Button
              onClick={() => {
                // Smart routing: Cat 10-12 → Sold Products page
                if (
                  effectiveScope3Category === 10 ||
                  effectiveScope3Category === 11 ||
                  effectiveScope3Category === 12
                ) {
                  void navigate({
                    to: '/ghg/scope3/sold-products',
                  });
                } else {
                  void navigate({
                    to: '/ghg/scope3/add',
                    search: {
                      ...(effectiveScope3Category != null
                        ? { scope3Category: effectiveScope3Category }
                        : {}),
                    },
                  });
                }
              }}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              {t('ghgScope3.addEntry')}
            </Button>
          </div>
        </header>

        {(effectiveScope3Category === 8 || effectiveScope3Category === 13) &&
          summary &&
          summary.totals.entryCount === 0 && (
            <Card className="border-blue-300 bg-blue-50/60">
              <CardContent className="flex items-start gap-3 pt-6">
                <Info className="mt-0.5 h-5 w-5 text-blue-700" />
                <div>
                  <p className="font-medium text-blue-900">
                    {effectiveScope3Category === 8 ? t('ghgScope3.cat8UpstreamLeasedAssets') : t('ghgScope3.cat13DownstreamLeasedAssets')}
                  </p>
                  <p className="text-sm text-blue-800">
                    {effectiveScope3Category === 8
                      ? t('ghgScope3.cat8Description')
                      : t('ghgScope3.cat13Description')}
                  </p>
                  <p className="mt-1 text-sm text-blue-700">
                    {t('ghgScope3.leasedAssetInstructions')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

        {summaryError && (
            <Card className="border-destructive/40">
              <CardContent className="pt-6 text-sm text-destructive">
                {t('ghgScope3.failedToLoadSummary')}
              </CardContent>
            </Card>
          )}

          {summary?.isBlocked && (
            <Card className="border-amber-400 bg-amber-50/60">
              <CardContent className="flex items-start gap-3 pt-6">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-700" />
                <div>
                  <p className="font-medium text-amber-900">{t('ghgScope3.missingEstimatesDetected')}</p>
                  <p className="text-sm text-amber-800">
                    {t('ghgScope3.missingEstimatesDescription')}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_entries')}</CardDescription>
                <CardTitle className="text-2xl">
                  {summaryLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    (summary?.totals.entryCount ?? 0)
                  )}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_estimated_emissions')}</CardDescription>
                <CardTitle className="text-2xl">
                  {summaryLoading ? (
                    <Skeleton className="h-8 w-32" />
                  ) : summary?.totals.estimatedCo2eT != null ? (
                    `${summary.totals.estimatedCo2eT.toLocaleString(undefined, { maximumFractionDigits: 3 })} tCO2e`
                  ) : (
                    t('ghgScope3.blocked')
                  )}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_data_quality_mix')}</CardDescription>
                <CardTitle className="text-sm font-normal text-muted-foreground">
                  {summaryLoading ? (
                    <Skeleton className="h-6 w-full" />
                  ) : (
                    t('ghgScope3.dataQualityMix', { measured: summary?.dataQualityCounts.measured ?? 0, calculated: summary?.dataQualityCounts.calculated ?? 0, estimated: summary?.dataQualityCounts.estimated ?? 0 })
                  )}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          {selectedCategoryMeta && selectedCategoryDetail && (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_visible_entries')}</CardDescription>
                    <CardTitle className="text-2xl">
                      {isLoading ? <Skeleton className="h-8 w-20" /> : visibleEntryStats.visibleEntries}
                    </CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_missing_estimate_visible')}</CardDescription>
                    <CardTitle className="text-2xl">
                      {isLoading ? (
                        <Skeleton className="h-8 w-20" />
                      ) : (
                        visibleEntryStats.missingEstimates
                      )}
                    </CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_approved_visible')}</CardDescription>
                    <CardTitle className="text-2xl">
                      {isLoading ? <Skeleton className="h-8 w-20" /> : visibleEntryStats.approvedEntries}
                    </CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_visible_emissions')}</CardDescription>
                    <CardTitle className="text-2xl">
                      {isLoading ? (
                        <Skeleton className="h-8 w-28" />
                      ) : (
                        `${(visibleEntryStats.visibleEstimatedCo2eKg / 1000).toLocaleString(undefined, {
                          maximumFractionDigits: 3,
                        })} tCO2e`
                      )}
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('ghgScope3CalculationsPage.cardTitle_category_guidance')}</CardTitle>
                  <CardDescription>{selectedCategoryDetail.summary}</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <CategoryGuidanceList title={t('ghgScope3CalculationsPage.title_in_scope')} items={selectedCategoryDetail.include} />
                  <CategoryGuidanceList
                    title={t('ghgScope3CalculationsPage.title_typical_activities')}
                    items={selectedCategoryDetail.typicalActivities}
                  />
                  <CategoryGuidanceList title={t('ghgScope3CalculationsPage.title_data_hints')} items={selectedCategoryDetail.dataHints} />
                  <CategoryGuidanceList
                    title={t('ghgScope3CalculationsPage.title_evidence_hints')}
                    items={selectedCategoryDetail.evidenceHints}
                  />
                  <CategoryGuidanceList
                    title={t('ghgScope3CalculationsPage.title_out_of_scope')}
                    items={selectedCategoryDetail.outOfScope}
                  />
                </CardContent>
              </Card>

              {selectedCategoryDetail.quickTemplates.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('ghgScope3CalculationsPage.cardTitle_quick_template_entry')}</CardTitle>
                    <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_start_with_a_pre_filled_template_for_thi')}</CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-3 md:grid-cols-2">
                    {selectedCategoryDetail.quickTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        className="rounded-lg border p-3 text-left hover:bg-muted/50"
                        onClick={() => {
                          void navigate({
                            to: '/ghg/scope3/add',
                            search: {
                              scope3Category: selectedCategoryMeta.number,
                              templateId: template.id,
                            },
                          });
                        }}
                      >
                        <p className="text-sm font-medium text-foreground">{template.label}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{template.description}</p>
                      </button>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {!selectedCategoryMeta && (
            <Card>
              <CardHeader>
                <CardTitle>{t('ghgScope3CalculationsPage.cardTitle_category_breakdown')}</CardTitle>
                <CardDescription>{t('ghgScope3CalculationsPage.cardDesc_ghg_protocol_scope_3_category_totals_fro')}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-5">
                {summaryLoading
                  ? Array.from({ length: 10 }).map((_, index) => (
                      <Skeleton key={index} className="h-24 w-full" />
                    ))
                  : (summary?.categoryBreakdown ?? []).map((bucket) => {
                      const categoryMeta = SCOPE3_CATEGORY_BY_NUMBER.get(bucket.categoryNumber);
                      return (
                        <button
                          key={bucket.categoryNumber}
                          type="button"
                          className="rounded-lg border p-3 text-left hover:bg-muted/50"
                          onClick={() => {
                            const slug = categoryMeta?.slug;
                            if (!slug) return;
                            void navigate({
                              to: '/ghg/scope3/$category',
                              params: { category: slug },
                            });
                          }}
                        >
                          <p className="text-xs text-muted-foreground">
                            Cat {bucket.categoryNumber} ·{' '}
                            {getScope3CategoryName(bucket.categoryNumber, t)}
                          </p>
                          <p className="mt-1 text-xl font-semibold">{bucket.entryCount}</p>
                          <p className="text-xs text-muted-foreground">
                            {bucket.estimatedCo2eKg != null
                              ? `${(bucket.estimatedCo2eKg / 1000).toLocaleString(undefined, {
                                  maximumFractionDigits: 3,
                                })} tCO2e`
                              : t('ghgScope3.blocked')}
                          </p>
                        </button>
                      );
                    })}
              </CardContent>
            </Card>
          )}

          <ScopeCalculationsEntriesCard
            scopeLabel={scopeLabel}
            periodName={period?.name}
            search={search}
            facilities={facilities}
            groups={groups}
            currentSortBy={currentSortBy}
            currentSortOrder={currentSortOrder}
            onFilterChange={onFilterChange}
            onPageChange={onPageChange}
            extraFilters={
              !selectedCategoryMeta ? (
                <Select
                  options={[
                    { value: '', label: t('ghgScope3.filter_all_categories') },
                    ...SCOPE3_CATEGORY_ROUTES.map((category) => ({
                      value: String(category.number),
                      label: `Cat ${String(category.number)}: ${getScope3CategoryName(category.number, t)}`,
                    })),
                  ]}
                  value={search.scope3Category == null ? '' : String(search.scope3Category)}
                  onChange={(value) => {
                    onFilterChange({
                      scope3Category: value === '' ? undefined : Number(value),
                    });
                  }}
                  className="w-full"
                />
              ) : undefined
            }
            entries={entries}
            total={total}
            page={page}
            pageSize={pageSize}
            totalPages={totalPages}
            isLoading={isLoading}
            isError={isError}
            error={error}
            deletePending={deleteEntryMutation.isPending}
            onDeleteSelected={async (entryIds) => {
              await Promise.all(
                entryIds.map(async (entryId) => deleteEntryMutation.mutateAsync(entryId))
              );
              await Promise.all([refetch(), refetchSummary()]);
            }}
            onRowClick={(entry) => {
              void navigate({
                to: '/ghg/activity-data/$entryId',
                params: { entryId: entry.id },
              });
            }}
            noEntriesTitle={
              selectedCategoryMeta
                ? t('ghgScope3.no_entries_category', { number: selectedCategoryMeta.number })
                : t('ghgScope3.no_entries_title')
            }
            noEntriesDescription={t('ghgScope3.no_entries_desc')}
          />

          {/* Upload Dialog for Cat 8/13 leased assets */}
          {(effectiveScope3Category === 8 || effectiveScope3Category === 13) && (
            <>
              <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle data-testid="upload-dialog-title">
                      {t('ghgScope3.upload_dialog_title')}
                    </DialogTitle>
                    <DialogDescription>
                      {t('ghgScope3.upload_dialog_desc')}
                    </DialogDescription>
                  </DialogHeader>
                  {ocrStatus.isPolling && activeUploadId ? (
                    <div className="flex flex-col items-center gap-3 py-8">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
                      <p className="text-sm text-muted-foreground">
                        {t('ghgScope3.processing_document')}
                      </p>
                    </div>
                  ) : (
                    <GhgDocumentUpload
                      reportingPeriodId={periodId}
                      defaultDocumentType="leased_assets_data"
                      onUploadComplete={(uploadId) => {
                        setActiveUploadId(uploadId);
                      }}
                      onError={() => {}}
                    />
                  )}
                </DialogContent>
              </Dialog>

              {activeUploadId && showReviewDialog && ocrStatus.extractedData && (
                <LeasedAssetReviewDialog
                  open={showReviewDialog}
                  onOpenChange={setShowReviewDialog}
                  uploadId={activeUploadId}
                  extractedAssets={
                    (ocrStatus.extractedData as Record<string, unknown>)?.assets
                      ? ((ocrStatus.extractedData as Record<string, unknown>).assets as Array<Record<string, unknown>>)
                      : []
                  }
                  scope3Category={effectiveScope3Category as 8 | 13}
                  reportingPeriodId={periodId ?? ''}
                  onSuccess={() => {
                    setShowReviewDialog(false);
                    setActiveUploadId(undefined);
                    void refetch();
                    void refetchSummary();
                  }}
                />
              )}
            </>
          )}
      </div>
    </div>
  );
}
